// statistics_clinic_03_practice_outliers_sampling_bias.md — 5 DIY practice
// cases mixing both outlier-detection methods (IQR fence, z-score) and three
// different sampling-bias sub-types (selection, survivorship, non-response).
// Same 9-field pattern as Statistics 1/2 so the answer form and grading stay
// consistent even though this topic isn't purely "pick a statistic."

export const STATS_CLINIC_03_TRACKER_ID = 'statistics-clinic-03-practice-bank';

export const STATS_CLINIC_03_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_03_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Shift manager alert',
    scenario: "A call centre supervisor is reviewing today's call-wait sample before deciding whether to flag anything to the shift manager. Most calls were answered quickly, but the supervisor wants an objective check before raising an alarm about any single call.",
    tables: [{
      columns: ['call_id', 'wait_seconds'],
      rows: [['K001', '45'], ['K002', '48'], ['K003', '50'], ['K004', '52'], ['K005', '55'], ['K006', '58'], ['K007', '60'], ['K008', '140']],
    }],
    answerGuide: {
      business_question: 'Did any call wait unusually long today?',
      statistic_family: 'Outlier detection.',
      statistic_chosen: 'IQR fence method.',
      why_statistic: 'Wait times could be skewed by one bad call; the IQR fence is robust to the very outlier it is checking for.',
      calculation: 'Sorted: 45,48,50,52,55,58,60,140. Q1 = (48+50)/2 = 49, Q3 = (58+60)/2 = 59, IQR = 10. Upper fence = 59 + 1.5x10 = 74.',
      result: 'K008 (140 seconds) exceeds the fence (74) — flagged as an outlier.',
      meaning: 'The other 7 calls all cluster between 45-60 seconds; K008 waited far longer.',
      decision: 'Flag K008 to the shift manager for investigation before including it in an average wait-time report.',
      caution: 'Do not delete the row — check whether the call was legitimately complex, a system glitch, or a data-entry error first.',
    },
    ruleTerms: {
      business_question: ['wait', 'unusually long'],
      statistic_family: ['outlier'],
      statistic_chosen: ['iqr', 'fence'],
      why_statistic: ['robust', 'skewed'],
      calculation: ['49', '59', '10', '74'],
      result: ['140', 'outlier', '74'],
      meaning: ['45', '60', 'far longer'],
      decision: ['flag', 'shift manager', 'investigate'],
      caution: ['do not delete', 'investigate'],
    },
    criticalTerms: { calculation: ['74'], result: ['140'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Sorting-belt safety check',
    scenario: "A logistics analyst is checking a batch of parcel weights before the courier's automated sorting system processes them. One wrong weight could route a parcel to the wrong conveyor belt, so the analyst wants an objective flag, not a guess.",
    tables: [{
      columns: ['parcel_id', 'weight_kg'],
      rows: [['P01', '2.0'], ['P02', '2.1'], ['P03', '1.9'], ['P04', '2.2'], ['P05', '2.0'], ['P06', '1.8'], ['P07', '2.1'], ['P08', '1.9'], ['P09', '2.0'], ['P10', '2.2'], ['P11', '15.0']],
    }],
    answerGuide: {
      business_question: 'Is any parcel weight in this batch unusual enough to check before automated sorting?',
      statistic_family: 'Outlier detection.',
      statistic_chosen: 'Z-score method.',
      why_statistic: 'Ten of the eleven weights cluster tightly and roughly symmetrically around a central value, which fits the z-score method well.',
      calculation: 'Mean = 35.2/11 = 3.2kg. Variance = 153.32/11 = 13.94. SD = sqrt(13.94) = 3.73kg. z for P11 (15kg) = (15-3.2)/3.73 = 3.16.',
      result: '|z| = 3.16, exceeds the |z| > 3 threshold — flagged as an outlier.',
      meaning: 'The 15kg parcel is far outside the normal 1.8-2.2kg range for this batch.',
      decision: 'Pull the 15kg parcel for manual verification before it reaches the automated sorting belt, in case the weight or label is wrong.',
      caution: 'The outlier itself inflated the mean and SD used to judge it (the masking effect) — cross-check with a plain look at the raw values too.',
    },
    ruleTerms: {
      business_question: ['weight', 'unusual', 'sorting'],
      statistic_family: ['outlier'],
      statistic_chosen: ['z-score', 'z score'],
      why_statistic: ['cluster', 'symmetric'],
      calculation: ['3.2', '3.73', '3.16'],
      result: ['3.16', 'outlier'],
      meaning: ['15kg', 'far outside'],
      decision: ['pull', 'manual verification'],
      caution: ['masking effect', 'inflated'],
    },
    criticalTerms: { calculation: ['3.16'], result: ['3.16'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: Board update on app ratings',
    scenario: 'A product manager is preparing a board update and wants to use the mobile app\'s headline app-store rating — 4.7 out of 5, from 200 reviews — as evidence that customers are satisfied overall.',
    tables: [{
      columns: ['metric', 'value'],
      rows: [['App-store rating', '4.7 / 5'], ['Number of reviews', '200'], ['Total app users (approx.)', '85,000']],
    }],
    answerGuide: {
      business_question: 'Does the 4.7/5 app-store rating represent overall customer satisfaction?',
      statistic_family: 'Sampling bias diagnosis.',
      statistic_chosen: 'Selection bias.',
      why_statistic: 'Only customers who chose to visit the app store and leave a review are represented; the vast majority of the ~85,000 users who never review are excluded.',
      calculation: 'App-store reviewers are self-selected — users are typically prompted or motivated to review only when they have a strongly positive or strongly negative experience. 200 reviews out of ~85,000 users is a tiny, non-random slice.',
      result: 'The 4.7/5 figure reflects the opinions of the small, self-selected group who chose to leave a review, not the full user base.',
      meaning: 'Most users never see a review prompt or choose to ignore it; their satisfaction is missing entirely from this number.',
      decision: 'Report 4.7/5 as "average rating among reviewers," not "customer satisfaction," and pair it with a separate, randomly-sampled in-app survey before the board update.',
      caution: 'Do not let a high, easy-to-access review score replace real satisfaction measurement just because it looks impressive.',
    },
    ruleTerms: {
      business_question: ['app-store rating', 'satisfaction'],
      statistic_family: ['sampling bias'],
      statistic_chosen: ['selection bias'],
      why_statistic: ['self-selected', 'excludes'],
      calculation: ['200', '85,000', 'self-selected'],
      result: ['reviewers', 'not', 'full user base'],
      meaning: ['missing', 'never review'],
      decision: ['average rating among reviewers', 'random', 'survey'],
      caution: ['do not', 'replace'],
    },
    criticalTerms: { statistic_chosen: ['selection'] },
  },
  {
    id: 'DIY04',
    title: 'DIY Practice 04: Quarterly engagement report',
    scenario: "An HR analyst ran this quarter's employee engagement survey. Only staff currently employed at survey time were included, and the result came back at 82%. The analyst is about to tell leadership that engagement is healthy.",
    tables: [{
      columns: ['metric', 'value'],
      rows: [['Engagement score (current staff)', '82%'], ['Employees surveyed', '340'], ['Employees who resigned this quarter', '28']],
    }],
    answerGuide: {
      business_question: 'Does the 82% engagement score reflect the true health of the team this quarter?',
      statistic_family: 'Sampling bias diagnosis.',
      statistic_chosen: 'Survivorship bias.',
      why_statistic: 'Employees who left the company during the quarter were not included in the survey at all, and disengaged employees are more likely to have already left.',
      calculation: 'The survey population is "employees still employed at survey time," which excludes all 28 people who resigned this quarter — their (likely lower) scores are entirely missing.',
      result: '82% is the average among survivors only, not among everyone who was on the team this quarter.',
      meaning: 'True quarter-wide engagement is likely lower than 82%, because the people most likely to report low engagement are the ones who already left.',
      decision: 'Report 82% as "engagement among current staff" and pair it with this quarter\'s attrition rate (28 resignations) and exit-interview sentiment if available.',
      caution: 'A healthy-looking survey score can hide a real engagement problem if the most disengaged people already left before the survey ran.',
    },
    ruleTerms: {
      business_question: ['engagement', 'health', 'quarter'],
      statistic_family: ['sampling bias'],
      statistic_chosen: ['survivorship bias'],
      why_statistic: ['left', 'resigned', 'excluded'],
      calculation: ['28', 'excludes', 'resigned'],
      result: ['survivors', 'not everyone'],
      meaning: ['lower', 'already left'],
      decision: ['current staff', 'attrition', 'exit-interview'],
      caution: ['hide', 'real engagement problem'],
    },
    criticalTerms: { statistic_chosen: ['survivorship'] },
  },
  {
    id: 'DIY05',
    title: 'DIY Practice 05: Post-delivery survey rollout',
    scenario: "An e-commerce operations lead emailed a post-delivery satisfaction survey to all 10,000 customers who ordered last month. 400 customers replied (a 4% response rate), averaging 4.8 out of 5. The lead wants to publish 4.8/5 as this month's headline delivery-satisfaction figure.",
    tables: [{
      columns: ['metric', 'value'],
      rows: [['Customers emailed', '10,000'], ['Responses received', '400'], ['Average score among responders', '4.8 / 5']],
    }],
    answerGuide: {
      business_question: 'Does the 4.8/5 average represent satisfaction across all of last month\'s customers?',
      statistic_family: 'Sampling bias diagnosis.',
      statistic_chosen: 'Non-response bias.',
      why_statistic: 'Only 4% of the 10,000 customers responded, and response is voluntary — responders are unlikely to be a random cross-section of all customers.',
      calculation: 'A 4% response rate is very low. Satisfaction surveys typically over-represent strongly-opinionated customers (very happy or very unhappy), while the mostly-neutral majority stays silent.',
      result: '4.8/5 reflects the 400 self-selected respondents, not the full 10,000 customers.',
      meaning: 'The true average across all 10,000 customers is unknown and could differ meaningfully, in either direction, from 4.8/5.',
      decision: 'Report 4.8/5 as "average among the 4% who responded," and consider a follow-up with a small random (not voluntary) sample for a more representative figure.',
      caution: 'A high average from a low response rate should never be presented to leadership as "customer satisfaction" without the response-rate caveat attached.',
    },
    ruleTerms: {
      business_question: ['satisfaction', 'all', 'customers'],
      statistic_family: ['sampling bias'],
      statistic_chosen: ['non-response bias', 'nonresponse'],
      why_statistic: ['4%', 'voluntary'],
      calculation: ['4%', 'low', 'over-represent'],
      result: ['400', 'not', '10,000'],
      meaning: ['unknown', 'could differ'],
      decision: ['response rate', 'random sample'],
      caution: ['never', 'without', 'caveat'],
    },
    criticalTerms: { statistic_chosen: ['non-response', 'nonresponse'] },
  },
];

export const STATS_CLINIC_03_CONFIG = {
  trackerId: STATS_CLINIC_03_TRACKER_ID,
  resourceKey: 'statistics_clinic_03_practice_outliers_sampling_bias',
  rubricKey: 'statistics_outliers_sampling_bias',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Outliers & Sampling Bias Cases',
  description: "Each case is a business request. Decide whether it's an outlier check (and which method fits) or a sampling-bias diagnosis, then show your reasoning. Your work autosaves to your own practice record.",
  introAlert: 'An outlier is found by looking at the numbers after collection. Sampling bias is found by questioning how the data was collected — no formula catches it.',
  lenses: STATS_CLINIC_03_LENSES,
  workingField: null,
  items: STATS_CLINIC_03_ITEMS,
};
